import React, { useEffect, useRef, useState } from 'react'

import AppBtn, { AppBtnColor, AppBtnSize } from '../../../components/btn/AppBtn'
import {
  useCheckTrainingProcessQuery,
  useLazyCheckLatestModelQuery,
  useEnableLatestModelMutation,
  useTrainModelMutation,
} from '../../../core/api/OrderApi'
import {
  AppNotification,
  NotificationType,
} from '../../../components/notification/Notification'
import {
  useGetTrainingAiProcessApiListQuery,
  useGetTrainingAiProcessApiDetailQuery,
} from 'core/api/TrainingAiProcessApi'
import { ModelTrainingLoader } from './ModelTrainingLoader'
import { ProgressBar } from './ProgressBar'
import { TrainingHistoryTable } from './TrainingHistoryTable'
import { useGetCurrentUserQuery } from 'core/api/UserApi'
import { Role } from 'core/api/dto/UserDto'

export enum DetailTab {
  'main' = 'main',
  'info' = 'info',
}

export const AiTab = () => {
  const [trainModel] = useTrainModelMutation()
  const [enableLatestModel] = useEnableLatestModelMutation()
  const { data: trainingData, refetch: refetchTrainingList } =
    useGetTrainingAiProcessApiListQuery()
  const { data: currentUser } = useGetCurrentUserQuery()
  const isAdministrator =
    currentUser?.userRoles.roleType.includes(Role.ADMINISTRATOR) ?? false
  const [isTrainingActive, setIsTrainingActive] = useState(() => {
    try {
      return localStorage.getItem('isTrainingActive') === 'true'
    } catch {
      return false
    }
  })
  //время последнего запуска обучения — нужно чтобы отличить запоздалые
  //404-ответы прошлой сессии от ошибок текущей и не гасить свежий запуск
  const lastTrainStartRef = useRef(0)

  const { data: trainingDataDetail, isError: isErrorTrainableModel } =
    useGetTrainingAiProcessApiDetailQuery(undefined, {
      pollingInterval: isTrainingActive ? 30000 : 0,
    })
  //этот блок нужен, чтобы сохранить время выполнения и начало обучения для передачи в прогресс бар
  const [savedStartTime, setSavedStartTime] = useState<number | null>(null)
  const [savedDurationMs, setSavedDurationMs] = useState<number | null>(null)

  useEffect(() => {
    if (trainingDataDetail?.startTime) {
      setIsTrainingActive(true)
      localStorage.setItem('isTrainingActive', 'true')
    }
  }, [trainingDataDetail])

  useEffect(() => {
    if (
      isTrainingActive &&
      trainingDataDetail?.startTime &&
      trainingDataDetail?.approximateTrainingTime != null &&
      savedStartTime === null &&
      savedDurationMs === null
    ) {
      const startMs = new Date(trainingDataDetail.startTime).getTime()
      const durationMs =
        Number(trainingDataDetail.approximateTrainingTime) * 1000

      if (!isNaN(startMs) && durationMs > 0) {
        setSavedStartTime(startMs)
        setSavedDurationMs(durationMs)
      }
    }
  }, [trainingDataDetail, savedStartTime, savedDurationMs])

  useEffect(() => {
    if (!isTrainingActive) {
      setSavedStartTime(null)
      setSavedDurationMs(null)
    }
  }, [isTrainingActive])

  const {
    isSuccess: isSuccessTrainings,
    isLoading: isTrainingLoading,
  } = useCheckTrainingProcessQuery(undefined, {
    skip: !isTrainingActive,
    pollingInterval: 30000,
  })

  const [checkLatestModel] = useLazyCheckLatestModelQuery()
  //конец обучения ловят два независимых опроса (ошибка detail и 200 от check_training_process),
  const finishingRef = useRef(false)

  //запоздалые 404 прошлой сессии (хвосты polling-таймеров) долетают двумя тиками с шагом 30с — окно 65с покрывает оба, чтобы не гасить свежий запуск
  const isStaleError = () => Date.now() - lastTrainStartRef.current < 65000

  const finishTraining = async () => {
    if (finishingRef.current) return
    finishingRef.current = true

    let isNewModel = false
    try {
      const res = await checkLatestModel().unwrap()
      isNewModel = res.detail === 'Доступна новая модель'
    } catch {
      //новой модели нет — старая остаётся на месте
    }

    if (isNewModel) {
      try {
        await enableLatestModel().unwrap()
        AppNotification({
          type: NotificationType.success,
          msg: 'Модель установлена',
        })
      } catch {
        AppNotification({
          type: NotificationType.error,
          msg: 'Ошибка установки модели',
        })
      }
    }

    setIsTrainingActive(false)
    localStorage.setItem('isTrainingActive', 'false')
    refetchTrainingList()
  }

  useEffect(() => {
    if (
      isTrainingActive &&
      (isErrorTrainableModel || isSuccessTrainings) &&
      !isStaleError()
    ) {
      finishTraining()
    }
  }, [isErrorTrainableModel, isSuccessTrainings, isTrainingActive])

  const handleTrain = () => {
    trainModel({
      num_epochs: 30,
    })
      .unwrap()
      .then(() => {
        AppNotification({
          type: NotificationType.success,
          msg: 'Обучение успешно запущено',
        })
        lastTrainStartRef.current = Date.now()
        finishingRef.current = false
        setIsTrainingActive(true)
        localStorage.setItem('isTrainingActive', 'true')
      })
  }

  return (
    <React.Fragment>
      <div>
        <div>
          {isAdministrator && !isTrainingActive && (
            <AppBtn
              onClick={handleTrain}
              iconClassName="learn-model"
              title="Обучить модель"
              sized={AppBtnSize.sm}
              color={AppBtnColor.violetWhite}
              disabled={isTrainingLoading || isTrainingActive}
              className="option-page-button"
            />
          )}
          {isAdministrator && isTrainingActive && (
            <ModelTrainingLoader isLoading={isTrainingActive} />
          )}

          {isAdministrator && isTrainingActive && (
            <ProgressBar
              title="Копирование данных"
              durationMs={savedDurationMs}
              startTime={savedStartTime}
            />
          )}

          <TrainingHistoryTable trainingData={trainingData} />
        </div>
      </div>
    </React.Fragment>
  )
}
